import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type ModuleKey =
  | "dashboard"
  | "media_registry"
  | "accreditation"
  | "events"
  | "coverage_analytics"
  | "awards"
  | "social_media"
  | "organogram"
  | "media_kits"
  | "journalist_portal"
  | "press_releases";

export type AccessLevel = "none" | "view" | "edit" | "full";

const LEVEL: Record<AccessLevel, number> = { none: 0, view: 1, edit: 2, full: 3 };

export interface MembershipSummary {
  orgId: string;
  orgName: string;
  orgType: string | null;
  logoUrl: string | null;
  roleId: string;
  roleName: string | null;
}

export interface OrgContextValue {
  loading: boolean;
  orgId: string | null;
  orgType: string | null;
  roleId: string | null;
  roleName: string | null;
  permissions: Record<ModuleKey, AccessLevel>;
  orgModuleFlags: Record<string, boolean>;
  can: (module: ModuleKey, level?: AccessLevel) => boolean;
  isPlanLimited: (module: ModuleKey) => boolean;
  refresh: () => Promise<void>;
  /** All institutions this user has active access to (for the org switcher). */
  availableOrgs: MembershipSummary[];
  /** True once there's more than one membership to switch between. */
  hasMultipleOrgs: boolean;
  /** Switch the active institution. Persists the choice for next session. */
  switchOrg: (orgId: string) => void;
}

const DEFAULT_PERMS = Object.fromEntries(
  (["dashboard", "media_registry", "accreditation", "events", "coverage_analytics", "awards", "social_media", "organogram", "media_kits", "journalist_portal", "press_releases"] as ModuleKey[]).map(k => [k, "none"]),
) as Record<ModuleKey, AccessLevel>;

const ACTIVE_ORG_STORAGE_KEY = "conorbit_active_org_id";

const OrgContext = createContext<OrgContextValue | null>(null);

// Conorbit's three distinct services. Every ModuleKey belongs to exactly one
// service-level org_modules flag, so toggling a service off in org settings
// gates every page under it, not just its own key.
const SERVICE_FLAG: Record<ModuleKey, string> = {
  dashboard: "dashboard",
  social_media: "social_media",
  coverage_analytics: "coverage_analytics",
  media_registry: "press_releases",
  accreditation: "press_releases",
  events: "press_releases",
  awards: "press_releases",
  organogram: "press_releases",
  media_kits: "press_releases",
  journalist_portal: "press_releases",
  press_releases: "press_releases",
};

export function OrgProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);
  const [orgId, setOrgIdState] = useState<string | null>(null);
  const [orgType, setOrgType] = useState<string | null>(null);
  const [roleId, setRoleId] = useState<string | null>(null);
  const [roleName, setRoleName] = useState<string | null>(null);
  const [permissions, setPermissions] = useState<Record<ModuleKey, AccessLevel>>(DEFAULT_PERMS);
  const [availableOrgs, setAvailableOrgs] = useState<MembershipSummary[]>([]);

  const { data: availableModules } = useQuery<string[]>({
    queryKey: ["plan-modules", orgId],
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data } = await (supabase.rpc("plan_modules" as any, {
        p_org_id: orgId,
      }) as any);
      return (data || []) as string[];
    },
  });

  // Org-level feature toggles (Social / Analytics / Press)
  const { data: orgModules } = useQuery<Record<string, boolean>>({
    queryKey: ["org-modules", orgId],
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from("org_modules")
        .select("module, enabled")
        .eq("org_id", orgId);
      const result: Record<string, boolean> = {};
      for (const row of data ?? []) result[row.module] = row.enabled;
      return result;
    },
  });

  const loadPermissionsFor = async (uid: string, membership: { org_id: string; role_id: string; roles?: { name: string } | null }) => {
    setOrgIdState(membership.org_id);
    setRoleId(membership.role_id);
    setRoleName(membership.roles?.name ?? null);

    const { data: org } = await (supabase
      .from("organizations" as any)
      .select("org_type")
      .eq("id", membership.org_id)
      .single() as any);
    setOrgType((org as any)?.org_type ?? null);

    const { data: perms } = await supabase
      .from("role_permissions" as any)
      .select("module, access_level")
      .eq("role_id", membership.role_id);

    const next = { ...DEFAULT_PERMS };
    (perms as any[] | null)?.forEach(p => {
      next[p.module as ModuleKey] = p.access_level as AccessLevel;
    });
    setPermissions(next);

    try {
      window.localStorage.setItem(ACTIVE_ORG_STORAGE_KEY, membership.org_id);
    } catch {
      // localStorage unavailable (private browsing, etc.) — switching just won't persist
    }
  };

  const load = async () => {
    setLoading(true);
    const { data: userData } = await supabase.auth.getUser();
    const user = userData.user;
    if (!user) {
      setLoading(false);
      return;
    }
    setUserId(user.id);

    const { data: memberships } = await supabase
      .from("organization_members" as any)
      .select("org_id, role_id, joined_at, roles(name), organizations(name, org_type, logo_url)")
      .eq("user_id", user.id)
      .eq("status", "active")
      .order("joined_at", { ascending: true });

    const rows = (memberships as any[] | null) ?? [];
    if (rows.length === 0) {
      setLoading(false);
      return;
    }

    const summaries: MembershipSummary[] = rows.map(m => ({
      orgId: m.org_id,
      orgName: m.organizations?.name ?? "Untitled institution",
      orgType: m.organizations?.org_type ?? null,
      logoUrl: m.organizations?.logo_url ?? null,
      roleId: m.role_id,
      roleName: m.roles?.name ?? null,
    }));
    setAvailableOrgs(summaries);

    let activeMembership = rows[0];
    try {
      const storedOrgId = window.localStorage.getItem(ACTIVE_ORG_STORAGE_KEY);
      if (storedOrgId) {
        const match = rows.find(m => m.org_id === storedOrgId);
        if (match) activeMembership = match;
      }
    } catch {
      // ignore
    }

    await loadPermissionsFor(user.id, activeMembership);
    setLoading(false);
  };

  useEffect(() => {
    load();

    // OrgProvider is mounted once at the app root (above the router), so it
    // never remounts on a soft-navigation sign-in/sign-out — without this
    // listener, orgId/permissions stay stuck at whatever they were when the
    // app first loaded (e.g. null/none while the user was still on /auth).
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN' || event === 'SIGNED_OUT') {
        load();
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  const switchOrg = (newOrgId: string) => {
    if (newOrgId === orgId || !userId) return;
    const target = availableOrgs.find(o => o.orgId === newOrgId);
    if (!target) return;

    setLoading(true);
    loadPermissionsFor(userId, { org_id: target.orgId, role_id: target.roleId, roles: { name: target.roleName ?? "" } })
      .then(() => {
        // Every org-scoped query elsewhere keys off orgId, so invalidating broadly
        // here guarantees no view shows stale data from the previous institution.
        queryClient.invalidateQueries();
        setLoading(false);
      });
  };

  const isPlanLimited = (module: ModuleKey): boolean => {
    if (!availableModules) return false;
    return !availableModules.includes(module);
  };

  const can = (module: ModuleKey, level: AccessLevel = "view"): boolean => {
    if (loading) return false;
    if (availableModules && !availableModules.includes(module)) return false;
    const serviceFlag = SERVICE_FLAG[module] ?? module;
    if (orgModules && orgModules[serviceFlag] === false) return false;
    return LEVEL[permissions[module] ?? "none"] >= LEVEL[level];
  };

  const value = useMemo<OrgContextValue>(() => ({
    loading,
    orgId,
    orgType,
    roleId,
    roleName,
    permissions,
    orgModuleFlags: orgModules ?? {},
    can,
    isPlanLimited,
    refresh: load,
    availableOrgs,
    hasMultipleOrgs: availableOrgs.length > 1,
    switchOrg,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [loading, orgId, orgType, roleId, roleName, permissions, orgModules, availableModules, availableOrgs]);

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
}

/** Drop-in replacement for the old standalone hook — same shape, now backed by shared context. */
export function useOrgPermissions(): OrgContextValue {
  const ctx = useContext(OrgContext);
  if (!ctx) {
    throw new Error("useOrgPermissions must be used within an OrgProvider");
  }
  return ctx;
}
